/**
 * The live court stage — the hero of the room. Everything on it comes from real
 * play-by-play the server has already applied: shots at their feed coordinates,
 * captions from the event, momentum from scoring plays. Official team logos and player
 * headshots (hotlinked from the NBA CDN, with fallbacks) give it a broadcast identity.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { formatClock, team, type GameEvent, type RoomSnapshot, type Side } from '@clutch/core';
import { Court } from './CourtView';
import { hasCoords, playCaption, type PlayCaption } from './court';
import { momentum } from './momentum';
import { friendChips, situationLine, type ReactionStack } from './social';
import { BEAT_MS, CAUGHT_UP_MS, type RewindStep } from './rewind';
import { initials, replayBadge, sideName, sideTricode } from '../room/derive';
import type { FloatingReaction } from '../room/useRoom';
import { Headshot, TeamLogo } from '../ui/Media';
import { gameStory, majorPlay } from './story';

export const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export type StageProps = {
  s: RoomSnapshot;
  fanId: string;
  plays: readonly GameEvent[];
  backfilled: boolean;
  liveSeq?: number;
  anchors: Map<string, ReactionStack[]>;
  reactions: FloatingReaction[];
  full: boolean;
  onToggleFull: () => void;
  /** Rewind / buzzer: ring this play and scrub the court to it. */
  focus?: { seq?: number; uptoSeq?: number };
  overlay?: ReactNode;
  footer?: ReactNode;
  /** Source badge; defaults to the replay badge. */
  badge?: { text: string; live: boolean; title?: string };
  /** Extra controls in the top bar (React cam toggle). */
  topActions?: ReactNode;
  /** Extra layers over the court (danmu lane). */
  layers?: ReactNode;
  /** Extra content on the caption's player card (take bubbles, "Watch the play"). */
  captionExtras?: (e: GameEvent) => ReactNode;
};

export function Stage(p: StageProps) {
  const { s, plays, full } = p;
  const vertical = useVertical(full);
  const teamName = (sd: Side) => sideName(s, sd);
  const uptoSeq = p.focus?.uptoSeq ?? s.state.asOfSeq;
  const latestShot = plays.filter((e) => e.type === 'shot' && e.seq <= uptoSeq).at(-1);

  const captionEvent = useMemo(() => {
    if (p.focus?.seq !== undefined) {
      const e = plays.find((x) => x.seq === p.focus!.seq);
      if (e && playCaption(e, teamName)) return e;
    }
    for (let i = plays.length - 1; i >= 0; i--) {
      const e = plays[i]!;
      if (e.seq <= uptoSeq && playCaption(e, teamName)) return e;
    }
    return undefined;
  }, [plays, uptoSeq, p.focus?.seq]);
  const story = useMemo(() => p.focus ? undefined : gameStory(s, plays, p.backfilled), [s.state, plays, p.backfilled, p.focus]);
  const major = !!captionEvent && captionEvent.seq === p.liveSeq && p.focus === undefined && majorPlay(captionEvent);
  const recapping = p.focus?.uptoSeq !== undefined;

  return (
    <section
      className={`arena${full ? ' is-full' : ''}${vertical ? ' is-vertical' : ''}${major ? ' has-major-play' : ''}`}
      aria-label="Live court"
      data-testid="stage"
    >
      <header className="arena__top">
        <FriendsRail s={s} fanId={p.fanId} />
        {p.topActions}
        <button
          className="arena__fs"
          onClick={p.onToggleFull}
          aria-label={full ? 'Exit full screen' : 'Full screen'}
          aria-pressed={full}
          data-testid="fullscreen-toggle"
        >
          {full ? <span aria-hidden="true">✕</span> : <ExpandIcon />}
        </button>
      </header>

      <ScoreBug s={s} />
      {recapping && <div className="arena__recap-note" data-testid="recap-context"><span>Catching you up</span><b>{p.badge?.live ? 'Live score above' : 'Current replay score above'}</b></div>}
      {story && <div className="game-pulse" data-testid="game-pulse" data-kind={story.kind} style={{ '--pulse': `var(--${story.side ?? 'home'})` } as CSSProperties}>
        <span className="game-pulse__label"><i aria-hidden="true" /> GAME PULSE</span>
        <p>{story.headline}</p>
        {!/^\d+ unanswered points\.$/.test(story.detail) && <span className="game-pulse__detail">{story.detail}</span>}
      </div>}

      <div className="arena__floor">
        <Court
          plays={plays}
          period={s.state.period}
          uptoSeq={uptoSeq}
          activeSeq={captionEvent?.seq ?? -1}
          {...(p.liveSeq !== undefined && p.focus === undefined ? { liveSeq: p.liveSeq } : {})}
          {...(p.focus?.seq !== undefined ? { highlightSeq: p.focus.seq } : {})}
          vertical={vertical}
          maxMarks={6}
        />
        <span
          className={`badge ${p.badge?.live && !recapping ? 'badge--live' : 'badge--replay'}`}
          data-testid="replay-badge"
          title={p.badge?.title ?? 'We replay the real play-by-play feed. The court is drawn from it.'}
        >
          <span className="badge__dot" />
          {recapping ? 'RECAP · earlier in this game' : p.badge?.text ?? replayBadge(s.replay.speed)}
        </span>
        <Floaters items={p.reactions} />
        {p.layers}
        {p.overlay}
      </div>
      <div className="court-legend" title="Shot locations from the game feed. Ball flight is an illustration, not live player tracking."><span><i className="court-legend__ball" />{latestShot ? captionEvent?.type !== 'shot' ? 'Recent shot locations' : hasCoords(latestShot) ? 'Latest shot · illustrated' : 'Shot location unavailable' : 'Waiting for the first shot'}</span><span>● Made <b>○ Missed</b></span></div>
      {captionEvent && (
          <Caption
            e={captionEvent}
            cap={playCaption(captionEvent, teamName)!}
            stacks={p.anchors.get(captionEvent.eventId)}
            fresh={major}
            recap={recapping}
            source={s.meta.source}
            extras={p.captionExtras?.(captionEvent)}
          />
        )}
      <p className="arena__situation" data-testid="situation">
        {situationLine(s)}
      </p>
      <MomentumStrip s={s} plays={plays} backfilled={p.backfilled} compact={!!story} />
      {p.footer}
    </section>
  );
}

function useVertical(full: boolean): boolean {
  const q = '(orientation: portrait)';
  const [portrait, setPortrait] = useState(() => typeof matchMedia !== 'undefined' && matchMedia(q).matches);
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const m = matchMedia(q);
    const on = () => setPortrait(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return full && portrait;
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path d="M3 8V3h5M17 8V3h-5M3 12v5h5M17 12v5h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ───────────── Score bug ─────────────

export function ScoreBug({ s }: { s: RoomSnapshot }) {
  const st = s.state;
  const final = st.status === 'final';
  const clock = formatClock(st.period, st.clockSec);
  const [per, time] = final ? [st.period > 4 ? `FINAL/${clock.split(' ')[0]}` : 'FINAL', ''] : clock.split(' ');
  const side = (sd: Side) => {
    const code = sideTricode(s, sd);
    const score = sd === 'home' ? st.homeScore : st.awayScore;
    const lead = sd === 'home' ? st.homeScore > st.awayScore : st.awayScore > st.homeScore;
    const poss = !final && st.possession === sd;
    return (
      <div className={`bug__team bug__team--${sd}${lead ? ' is-lead' : ''}`} aria-label={`${team(code).name} ${score}`}>
        <span className="bug__bar" />
        <TeamLogo tricode={code} teamId={sd === 'home' ? s.meta.home.teamId : s.meta.away.teamId} size={30} className="bug__logo" />
        <span className="bug__code">
          {code}
          {poss && <span className="bug__poss" title="Possession" />}
        </span>
        <span className="bug__score" key={score} data-testid={`score-${sd}`}>
          {score}
        </span>
      </div>
    );
  };
  return (
    <div className="bug" aria-label="Score">
      {side('away')}
      {side('home')}
      <div className="bug__clock" data-testid="clock">
        <span className="bug__period">{per}</span>
        {time && <span className="bug__time">{time}</span>}
      </div>
    </div>
  );
}

// ───────────── Friends on the stage ─────────────

function FriendsRail({ s, fanId }: { s: RoomSnapshot; fanId: string }) {
  const chips = friendChips(s, fanId, initials);
  const shown = chips.slice(0, 3);
  const more = chips.length - shown.length;
  return (
    <div className="rail" aria-label="Who's here">
      <ul className="rail__list">
        {shown.map((c) => (
          <li
            key={c.fanId}
            className={`rail__fan${c.me ? ' is-me' : ''}${c.callSide ? ' has-call' : ''}`}
            style={{ '--call': c.callSide ? `var(--${c.callSide})` : 'var(--line-strong)' } as CSSProperties}
            title={c.callLabel ? `${c.name} called ${c.callLabel}` : c.name}
          >
            <span className="rail__av">{c.initials}</span>
            <span className="rail__txt">
              <span className="rail__name">
                {c.me ? 'You' : c.name.replace(/ \(bot\)$/, '')}
                {/\(bot\)$/.test(c.name) && <i className="rail__bot">bot</i>}
              </span>
              <span className="rail__call">{c.callLabel ?? 'no call'}</span>
            </span>
          </li>
        ))}
        {more > 0 && <li className="rail__more">+{more}</li>}
      </ul>
      <span className="rail__count">{chips.length} here</span>
    </div>
  );
}

// ───────────── Caption ─────────────

function Caption({
  e,
  cap,
  stacks,
  fresh,
  recap,
  source,
  extras,
}: {
  e: GameEvent;
  cap: PlayCaption;
  stacks?: ReactionStack[];
  fresh: boolean;
  recap: boolean;
  source: RoomSnapshot['meta']['source'];
  extras?: ReactNode;
}) {
  const person = (e.personId || e.playerName) && (e.type === 'shot' || e.type === 'freethrow' || e.type === 'steal' || e.type === 'block' || e.type === 'turnover');
  return (
    <div className="caption-wrap" key={e.seq}>
      <div
        className={`caption caption--${cap.tone}${fresh ? ' is-fresh' : ''}${person ? ' has-player' : ''}`}
        style={{ '--cap': cap.side ? `var(--${cap.side})` : 'var(--muted)' } as CSSProperties}
        data-testid="play-caption"
        aria-live="polite"
      >
        {person && <Headshot personId={e.personId} source={source} name={e.playerName} size={60} ring={cap.side ? `var(--${cap.side})` : undefined} className="caption__head" />}
        <span className="caption__main">
          <span className="caption__eyebrow">{recap ? 'RECAP PLAY' : fresh ? 'BIG PLAY' : 'LATEST PLAY'}</span>
          <span className="caption__text">{cap.text}</span>
          <span className="caption__clock">{formatClock(e.period, e.clockSec)}</span>
        </span>
        {cap.tone === 'make' && cap.points ? <span className="caption__pts">+{cap.points}</span> : null}
        {stacks && stacks.length > 0 && <Stacks stacks={stacks} />}
      </div>
      {extras}
    </div>
  );
}

export function Stacks({ stacks }: { stacks: ReactionStack[] }) {
  return (
    <span className="stacks" aria-label={stacks.map((x) => `${x.emoji} ${x.count}`).join(', ')}>
      {stacks.slice(0, 3).map((x) => (
        <span key={x.emoji} className={`stacks__one${x.mine ? ' is-mine' : ''}`} title={x.names.join(', ')}>
          {x.emoji}
          {x.count > 1 && <b>{x.count}</b>}
        </span>
      ))}
    </span>
  );
}

// ───────────── Momentum ─────────────

function MomentumStrip({ s, plays, backfilled, compact }: { s: RoomSnapshot; plays: readonly GameEvent[]; backfilled: boolean; compact: boolean }) {
  const m = useMemo(
    () => momentum(plays, s.state, backfilled ? { logStartSeq: 0 } : { logStartSeq: -1 }),
    [plays, s.state, backfilled],
  );
  if (s.state.status === 'pre') return null;
  const code = (sd: Side) => sideTricode(s, sd);
  return (
    <div className={`mo${compact ? ' mo--compact' : ''}`} data-testid="momentum" aria-label={`Last 2 minutes: ${code('away')} ${m.away}, ${code('home')} ${m.home}`}>
      <div className="mo__head">
        <span className="mo__label">{m.complete ? 'LAST 2:00' : 'SINCE YOU JOINED'}</span>
        <span className="mo__tot">
          <b style={{ color: 'var(--away)' }}>{code('away')}</b> {m.away}
          <span className="mo__dash">–</span>
          {m.home} <b style={{ color: 'var(--home)' }}>{code('home')}</b>
        </span>
        {m.run && (
          <span className="mo__run" style={{ '--run': `var(--${m.run.side})` } as CSSProperties}>
            {sideName(s, m.run.side)} {m.run.points}–0 run
          </span>
        )}
      </div>
      <svg className="mo__track" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">
        <line x1="0" x2="100" y1="10" y2="10" className="mo__mid" />
        {m.ticks.map((t) => {
          const h = 2.5 * t.points + 1.5;
          const x = 1 + t.at * 98;
          return (
            <rect
              key={t.seq}
              x={x - 0.9}
              width={1.8}
              y={t.side === 'away' ? 10 - h : 10}
              height={h}
              rx={0.4}
              style={{ fill: `var(--${t.side})` }}
            />
          );
        })}
        <line x1="99.5" x2="99.5" y1="1" y2="19" className="mo__now" />
      </svg>
    </div>
  );
}

// ───────────── Floating reactions (over the court) ─────────────

function Floaters({ items }: { items: FloatingReaction[] }) {
  return (
    <div className="floaters" aria-hidden="true">
      {items.map((r) => (
        <span key={r.id} className={`floater${r.mine ? ' is-mine' : ''}`} style={{ left: `${r.x}%` }}>
          <span className="floater__emoji">{r.emoji}</span>
          <span className="floater__name">{r.mine ? 'You' : r.name}</span>
        </span>
      ))}
    </div>
  );
}

// ───────────── Rewind ─────────────

/** Auto-advances through steps (BEAT_MS each); tap → next; Skip → done. */
export function useRewindPlayer(steps: RewindStep[], onDone: () => void) {
  const [i, setI] = useState(0);
  const done = useRef(onDone);
  done.current = onDone;
  const active = steps.length > 0 && i < steps.length;
  useEffect(() => {
    if (!active) return;
    const step = steps[i]!;
    const t = setTimeout(() => setI((x) => x + 1), step.kind === 'caught_up' ? CAUGHT_UP_MS : BEAT_MS);
    return () => clearTimeout(t);
  }, [active, i, steps]);
  useEffect(() => {
    if (steps.length > 0 && i >= steps.length) done.current();
  }, [i, steps.length]);
  return {
    step: active ? steps[i] : undefined,
    index: i,
    next: () => setI((x) => x + 1),
    skip: () => setI(steps.length),
  };
}

export function RewindOverlay({
  steps,
  index,
  plays,
  s,
  onNext,
  onSkip,
}: {
  steps: RewindStep[];
  index: number;
  plays: readonly GameEvent[];
  s: RoomSnapshot;
  onNext: () => void;
  onSkip: () => void;
}) {
  const step = steps[index]!;
  const ev = step.seq !== undefined ? plays.find((e) => e.seq === step.seq) : undefined;
  const beatCount = steps.filter((x) => x.kind !== 'caught_up').length;
  const at = step.beat ? formatClock(step.beat.period, step.beat.clockSec) : undefined;
  const bars = (
      <div className="rewind__bars" aria-hidden="true">
        {steps
          .filter((x) => x.kind !== 'caught_up')
          .map((_, k) => (
            <span key={k} className={`rewind__bar${k < index ? ' is-done' : k === index ? ' is-on' : ''}`}>
              <i style={{ animationDuration: `${BEAT_MS}ms` }} />
            </span>
          ))}
      </div>
  );
  return (
    <div
      className={`rewind${step.kind === 'caught_up' ? ' is-done' : ''}`}
      data-testid="rewind"
      role="button"
      tabIndex={0}
      aria-label="What you missed — tap for next"
      onClick={onNext}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onNext()}
    >
      <div className="rewind__body">
        {bars}
        {ev?.personId && step.kind !== 'now' && step.kind !== 'caught_up' && (
          <Headshot personId={ev.personId} source={s.meta.source} name={ev.playerName} size={64} ring={ev.side ? `var(--${ev.side})` : undefined} className="rewind__head" />
        )}
        <p className="rewind__eyebrow">
          {step.kind === 'caught_up' ? 'BACK TO THE GAME' : `WHAT YOU MISSED · ${Math.min(index + 1, beatCount)}/${beatCount}`}
          {at && step.kind !== 'now' && <span className="rewind__at"> · {at}</span>}
          {ev && step.kind !== 'now' && step.kind !== 'caught_up' && (
            <span className="rewind__at">
              {' '}
              · THEN: {s.meta.away.tricode} {ev.awayScore}–{ev.homeScore} {s.meta.home.tricode}
            </span>
          )}
        </p>
        <p className="rewind__text" data-testid="rewind-beat" key={index}>
          {step.text}
        </p>
      </div>
      <button
        className="rewind__skip"
        data-testid="rewind-skip"
        onClick={(e) => {
          e.stopPropagation();
          onSkip();
        }}
      >
        {step.kind === 'caught_up' ? 'Go' : 'Skip'}
      </button>
    </div>
  );
}

// ───────────── Buzzer (lands on the court before the result card) ─────────────

export function BuzzerOverlay({ s }: { s: RoomSnapshot }) {
  const res = s.resolution!;
  const winner: Side = res.finalHome > res.finalAway ? 'home' : 'away';
  return (
    <div className="buzzer" data-testid="buzzer" style={{ '--win': `var(--${winner})` } as CSSProperties}>
      <span className="buzzer__final">FINAL</span>
      <span className="buzzer__score">
        {s.meta.away.tricode} {res.finalAway} – {res.finalHome} {s.meta.home.tricode}
      </span>
    </div>
  );
}

/** The final play to ring at the buzzer: the game's last shot or free throw, made or missed. */
export function finalPlay(plays: readonly GameEvent[]): GameEvent | undefined {
  for (let i = plays.length - 1; i >= 0; i--) {
    const e = plays[i]!;
    if (e.type === 'shot' || e.type === 'freethrow') return e;
  }
  return undefined;
}
